import { Product } from '../types/store';
import { PRODUCTS as DEFAULT_PRODUCTS } from '../data/products';

/**
 * Convert arbitrary product title or flavor to SEO-friendly slug
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

/**
 * Get canonical slug for a product
 */
export function getProductSlug(product: Partial<Product>): string {
  if (product.slug && product.slug.trim()) {
    return slugify(product.slug);
  }

  if (product.id === 'malrich-buy2-get1' || product.title?.toLowerCase().includes('buy 2')) {
    return 'malrich-buy-2-get-1-free-bundle';
  }

  if (product.id === 'malrich-family-bundle' || product.title?.toLowerCase().includes('family')) {
    return 'malrich-family-bundle';
  }

  if (product.title) {
    return slugify(product.title);
  }

  if (product.flavor) {
    return `malrich-${slugify(product.flavor)}`;
  }

  if (product.id) {
    return slugify(product.id);
  }

  return 'malrich-supplement';
}

/**
 * Get direct, full working URL for an ad destination (TikTok / Meta Ads)
 */
export function getProductLiveUrl(product: Product, customBaseUrl?: string): string {
  let origin = 'https://healthstore.store';

  if (customBaseUrl && customBaseUrl.trim()) {
    origin = customBaseUrl.trim().replace(/\/+$/, '');
  } else if (typeof window !== 'undefined' && window.location.origin) {
    const winOrigin = window.location.origin;
    if (winOrigin.includes('ais-dev-')) {
      // ais-dev is private to AI Studio developer session.
      // Auto-convert to public shared domain or custom domain so ads and incognito test never 403!
      origin = winOrigin.replace('ais-dev-', 'ais-pre-');
    } else if (winOrigin.includes('localhost')) {
      origin = 'https://healthstore.store';
    } else {
      origin = winOrigin;
    }
  }

  const slug = getProductSlug(product);
  return `${origin}/products/${slug}`;
}

/**
 * Robust matching for direct landing traffic
 * Supports:
 * - Exact slug (e.g. "malrich-buy-2-get-1-free-bundle", "malrich-healthy-weight-gain-vanilla")
 * - Bundle deal aliases (e.g. "buy-2-get-1", "buy2get1", "bundle", "family-bundle")
 * - Flavor names (e.g. "vanilla", "mango", "chocolate", "strawberry")
 * - Product ID (e.g. "malrich-buy2-get1", "malrich-vanilla")
 * - Partial clean titles
 * - Fallback to static catalog if dynamic DB list is syncing or partial
 */
export function findProductBySlugOrParam(
  products: Product[],
  target: string
): Product | undefined {
  if (!target) return undefined;

  // Build unified catalog merging dynamic Firestore products with default catalog
  const catalogMap = new Map<string, Product>();
  DEFAULT_PRODUCTS.forEach((p) => catalogMap.set(p.id, p));
  if (Array.isArray(products) && products.length > 0) {
    products.forEach((p) => catalogMap.set(p.id, p));
  }
  const allProducts = Array.from(catalogMap.values());

  const rawClean = target.trim().toLowerCase();
  const alphaClean = rawClean.replace(/[^a-z0-9]/g, '');

  // 1. Direct slug match
  const exactSlug = allProducts.find(
    (p) => p.slug && p.slug.trim().toLowerCase() === rawClean
  );
  if (exactSlug) return exactSlug;

  // 2. Direct ID match
  const exactId = allProducts.find(
    (p) => p.id && p.id.trim().toLowerCase() === rawClean
  );
  if (exactId) return exactId;

  // 3. Known bundle aliases (specifically for TikTok / Meta campaign links)
  if (
    rawClean.includes('buy-2') ||
    rawClean.includes('buy2') ||
    alphaClean.includes('buy2get1') ||
    alphaClean.includes('buy2') ||
    rawClean.includes('free-bundle') ||
    rawClean === 'bundle' ||
    rawClean === 'bundles'
  ) {
    const buy2Bundle = allProducts.find(
      (p) => p.id === 'malrich-buy2-get1' || p.slug?.includes('buy-2-get-1') || p.title?.toLowerCase().includes('buy 2')
    );
    if (buy2Bundle) return buy2Bundle;
  }

  if (rawClean.includes('family') || alphaClean.includes('familybundle')) {
    const familyBundle = allProducts.find(
      (p) => p.id === 'malrich-family-bundle' || p.slug?.includes('family') || p.title?.toLowerCase().includes('family')
    );
    if (familyBundle) return familyBundle;
  }

  // 4. Normalized alphanumeric slug or ID match
  const alphaMatch = allProducts.find((p) => {
    const pSlugClean = p.slug ? p.slug.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    const pIdClean = p.id ? p.id.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    return pSlugClean === alphaClean || pIdClean === alphaClean;
  });
  if (alphaMatch) return alphaMatch;

  // 5. Flavor-specific alias matches (e.g. /products/vanilla or /products/malrich-vanilla)
  const flavorMatch = allProducts.find((p) => {
    const flavor = p.flavor?.toLowerCase() || '';
    if (!flavor || p.isBundle) return false;
    return (
      rawClean === flavor ||
      rawClean === `malrich-${flavor}` ||
      alphaClean === flavor ||
      alphaClean === `malrich${flavor}` ||
      rawClean.includes(flavor)
    );
  });
  if (flavorMatch) return flavorMatch;

  // 6. Title substring or slugified title match
  const titleMatch = allProducts.find((p) => {
    const generatedSlug = slugify(p.title || '');
    if (generatedSlug === rawClean) return true;
    const titleClean = (p.title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return titleClean.includes(alphaClean) || alphaClean.includes(titleClean);
  });
  if (titleMatch) return titleMatch;

  // 7. If target is non-empty, provide the flagship best seller to prevent 404 on paid ad clicks
  const flagship = allProducts.find((p) => p.id === 'malrich-vanilla') || allProducts[0];
  return flagship;
}

/**
 * Copy product URL to clipboard with fallback
 */
export async function copyProductLinkToClipboard(
  product: Product,
  customBaseUrl?: string
): Promise<{ success: boolean; url: string }> {
  const url = getProductLiveUrl(product, customBaseUrl);

  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(url);
      return { success: true, url };
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return { success: true, url };
    }
  } catch (err) {
    console.error('Failed to copy product link', err);
    return { success: false, url };
  }
}
